import {
    Dialog,
    DialogHeader,
    DialogBody,
    DialogFooter,
    Typography,
} from "@material-tailwind/react";
import { useEffect, useState } from "react";
import { XCircleIcon } from "@heroicons/react/24/solid";
import { useDispatch, useSelector } from "react-redux";
import ShowLoader from "@/components/Loader";
import Select from "react-select";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import { assignOrderToFinanceMember, fetchFinanceTeamMember, setEmptyFinanceTeamMember, resetStatus } from "@/store/slices/orderSlice";
import Toaster from "@/components/Toaster";
import { useTranslation, Trans } from "react-i18next";
import moment from "moment";
import { getRfqCommission } from "@/store/slices/rfqSlice";

const customSelectStyles = {
    control: (provided, state) => ({
        ...provided,
        borderRadius: '8px',
        minHeight: '44px',
        borderColor: state.isFocused ? '#212121' : '#b0bec5',
        boxShadow: state.isFocused ? '0 0 0 1px #212121' : 'none',
        '&:hover': {
            borderColor: state.isFocused ? '#212121' : '#b0bec5',
        },
    }),
    placeholder: (provided) => ({
        ...provided,
        color: '#9e9e9e',
    }),
    menu: (provided) => ({
        ...provided,
        zIndex: 50,
        borderRadius: '8px',
    })
};

const roleLabel = "Finance";

// NOTE: removed the "from >= manager_from" test — Finance Rep's slot is
// designed to occur BEFORE the Finance Manager's slot (rep's "To" auto-fills
// manager's "From" in MoveToOrdersModal), so that lower-bound check was
// structurally incorrect and caused false validation errors.
const schema = yup.object().shape({
    finance_member_id: yup.string().required("finance_member_required"),

    time_slot_from: yup
        .string()
        .nullable()
        .when("finance_member_id", {
            is: (value) => !!value,
            then: (s) =>
                s.required(`timeslot_required#{"role":"${roleLabel}"}`)
                    .test("from<=manager_to", `timeslot_exceed_deadline_error#{"role":"${roleLabel}","direction":"From"}`, function (v, context) {
                        const managerTo = context.options.context?.manager_to;
                        if (!v || !managerTo) return true;
                        return new Date(v).getTime() <= new Date(managerTo).getTime();
                    }),
            otherwise: (s) => s.notRequired(),
        }),

    time_slot_to: yup
        .string()
        .nullable()
        .when("finance_member_id", {
            is: (value) => !!value,
            then: (s) =>
                s.when("time_slot_from", {
                    is: (from) => !!from,
                    then: (ss) =>
                        ss.required(`timeslot_required#{"role":"${roleLabel}"}`)
                            .test("to>=from", `timeslot_to_before_from_error#{"role":"${roleLabel}"}`, function (v) {
                                const from = this.parent?.time_slot_from;
                                if (!v || !from) return true;
                                return new Date(v).getTime() >= new Date(from).getTime();
                            })
                            .test("to<=manager_to", `timeslot_exceed_deadline_error#{"role":"${roleLabel}","direction":"To"}`, function (v, context) {
                                const managerTo = context.options.context?.manager_to;
                                if (!v || !managerTo) return true;
                                return new Date(v).getTime() <= new Date(managerTo).getTime();
                            }),
                    otherwise: (ss) => ss.notRequired(),
                }),
            otherwise: (s) => s.notRequired(),
        }),
});

const formatForDateTimeInput = (dateStr) => {
    if (!dateStr) return "";
    const formatted = dateStr.replace(" ", "T");
    return formatted.length >= 16 ? formatted.slice(0, 16) : formatted;
};

export function AssignFinanceTeamMemberModal({ isOpen, onClose, orderId, assigned_details, rfqId }) {
    const dispatch = useDispatch();
    const [leaveConfirmation, setLeaveConfirmation] = useState(null);
    const [rfqManagerSlot, setRfqManagerSlot] = useState({ from: null, to: null });

    const { t } = useTranslation('order');
    const { t: ct } = useTranslation("common");

    const managerDetails = Array.isArray(assigned_details)
        ? assigned_details.find((d) => d.role === "Finance" && d.designation === "Manager")
        : null;

    const managerFromDate = managerDetails?.timeslot?.from || rfqManagerSlot.from;
    const managerToDate = managerDetails?.timeslot?.to || rfqManagerSlot.to;

    const initialValue = {
        order_id: orderId,
        finance_member_id: null,
        time_slot_from: "",
        time_slot_to: "",
    };

    const {
        register,
        handleSubmit,
        setValue,
        getValues,
        watch,
        formState: { errors },
        reset,
    } = useForm({
        defaultValues: initialValue,
        resolver: yupResolver(schema),
        context: { manager_from: managerFromDate, manager_to: managerToDate }
    });

    const { finance_team, loading } = useSelector((state) => state.order);

    // Auto-fill logic — UNCHANGED, this part is correct.
    // Fetch pre-existing Finance Rep timeslot from RFQ commission data
    // and display it (this data was set earlier via MoveToOrdersModal).
    useEffect(() => {
        if (isOpen && orderId) {
            dispatch(fetchFinanceTeamMember(orderId));
        }

        if (isOpen && rfqId) {
            dispatch(getRfqCommission({ rfq_id: rfqId }))
                .unwrap()
                .then((res) => {
                    const commissionData = res?.data || res;

                    if (commissionData) {
                        setRfqManagerSlot({
                            from: commissionData.finance_manager_time_slot_from,
                            to: commissionData.finance_manager_time_slot_to
                        });

                        const formattedFrom = formatForDateTimeInput(commissionData.finance_rep_time_slot_from);
                        const formattedTo = formatForDateTimeInput(commissionData.finance_rep_time_slot_to);

                        if (formattedFrom) {
                            setValue("time_slot_from", formattedFrom, { shouldValidate: true });
                        }
                        if (formattedTo) {
                            setValue("time_slot_to", formattedTo, { shouldValidate: true });
                        }
                    }
                })
                .catch((err) => {
                    console.error("RFQ commission fetch error:", err);
                });
        }

        return () => {
            dispatch(setEmptyFinanceTeamMember());
        };
    }, [isOpen, orderId, rfqId, dispatch, setValue]);

    // FIX: payload now matches the confirmed API contract — { order_id, assigned_to }
    const onSubmit = async (data) => {
        try {
            const payload = {
                order_id: data.order_id,
                finance_member_id: data.finance_member_id, // mapped to assigned_to inside the thunk
            };
            const result = await dispatch(assignOrderToFinanceMember(payload)).unwrap();
            if (result?.status) {
                Toaster.success(result.message || ct("assignSuccess"));
                dispatch(resetStatus());
                reset(initialValue);
                onClose();
            } else if (!result?.status && result?.data?.users_on_leave) {
                setLeaveConfirmation(result.data.users_on_leave);
                Toaster.error(result.message || ct("assignOnLeaveError"));
                dispatch(resetStatus());
            } else {
                Toaster.error(result?.message || ct("somethingWentWrong"));
                dispatch(resetStatus());
            }
        } catch (err) {
            Toaster.error(err?.message || ct("somethingWentWrong"));
            dispatch(resetStatus());
        }
    };

    const handleForceAssign = async () => {
        try {
            const values = getValues();
            const payload = {
                order_id: values.order_id,
                finance_member_id: values.finance_member_id,
                force_assign: true,
            };
            const result = await dispatch(assignOrderToFinanceMember(payload)).unwrap();
            if (result?.status) {
                Toaster.success(result.message || ct("assignSuccess"));
                dispatch(resetStatus());
                reset(initialValue);
                onClose();
                setLeaveConfirmation(null);
            } else {
                setLeaveConfirmation(null);
                Toaster.error(result.message || ct("somethingWentWrong"));
                dispatch(resetStatus());
            }
        } catch (err) {
            setLeaveConfirmation(null);
            Toaster.error(err?.message || ct("assignFailedError"));
            dispatch(resetStatus());
        }
    };

    // FIX: date inputs now bounded (min/max) and disabled until a member is
    // selected — same pattern as RFQ's AssignTeamMemberModal. This stops
    // "all dates open" from the calendar picker.
    const now = new Date();
    const minDateTime = now.toISOString().slice(0, 16);
    const effectiveMaxToDateTime = managerToDate ? new Date(managerToDate).toISOString().slice(0, 16) : undefined;

    const timeSlotFrom = watch("time_slot_from");
    const timeSlotTo = watch("time_slot_to");
    const isMemberSelected = !!watch("finance_member_id");

    return (
        <Dialog open={isOpen} handler={() => { setLeaveConfirmation(null); onClose(); }} size="md" className="rounded-2xl p-0">
            {loading && <ShowLoader />}

            <DialogHeader className="flex justify-between items-center px-6 py-4 border-b">
                <h2 className="text-2xl font-bold text-gray-900">
                    {t('assign_team_member')}
                </h2>
            </DialogHeader>

            <form onSubmit={handleSubmit(onSubmit)}>
                <div className="px-6 max-h-[65vh] overflow-y-auto pt-4 pb-6">
                    <DialogBody className="p-0">
                        <div className="space-y-6">
                            <div>
                                <Typography variant="small" className="mb-2 font-medium capitalize">
                                    {t("finance_team_members_label")} <span className="text-red-500">*</span>
                                </Typography>
                                <Select
                                    placeholder={t("select_finance_member_placeholder")}
                                    options={finance_team}
                                    onChange={(m) => {
                                        setValue("finance_member_id", m ? m.value : null, {
                                            shouldValidate: true
                                        });
                                    }}
                                    styles={customSelectStyles}
                                    isClearable
                                    noOptionsMessage={() => t("no_finance_member_available")}
                                    value={finance_team?.find(option => option.value === watch("finance_member_id")) || null}
                                />
                                {errors.finance_member_id && (
                                    <p className="text-red-500 text-sm mt-1">{t(errors.finance_member_id.message)}</p>
                                )}
                            </div>

                            <div>
                                <div className="bg-gray-50 rounded-md border border-gray-200">
                                    <div className="flex flex-col md:flex-row items-center justify-between px-4 py-3 gap-3 md:gap-0">
                                        <span className="text-gray-800 text-sm font-medium whitespace-nowrap capitalize w-full md:w-auto">
                                            {t("timeslot_label")} <span className="text-red-500">*</span>
                                        </span>
                                        <div className="flex items-center gap-2 w-full md:w-auto">
                                            <input
                                                type="datetime-local"
                                                disabled={!isMemberSelected}
                                                min={minDateTime}
                                                max={effectiveMaxToDateTime}
                                                {...register("time_slot_from")}
                                                className={`w-full md:w-[175px] text-center border rounded-md py-1.5 text-sm outline-none focus:ring-1 focus:ring-gray-900 ${errors.time_slot_from ? "border-red-500" : "border-gray-300"} ${!isMemberSelected ? "bg-gray-100 cursor-not-allowed text-gray-400" : ""}`}
                                            />
                                            <input
                                                type="datetime-local"
                                                disabled={!isMemberSelected}
                                                min={timeSlotFrom || minDateTime}
                                                max={effectiveMaxToDateTime}
                                                {...register("time_slot_to")}
                                                className={`w-full md:w-[175px] text-center border rounded-md py-1.5 text-sm outline-none focus:ring-1 focus:ring-gray-900 ${errors.time_slot_to ? "border-red-500" : "border-gray-300"} ${!isMemberSelected ? "bg-gray-100 cursor-not-allowed text-gray-400" : ""}`}
                                            />
                                            {(timeSlotFrom || timeSlotTo) && (
                                                <XCircleIcon
                                                    className="h-5 w-5 text-red-500 cursor-pointer hover:text-red-700 transition-colors flex-shrink-0"
                                                    onClick={() => {
                                                        setValue("time_slot_from", "", { shouldValidate: true });
                                                        setValue("time_slot_to", "", { shouldValidate: true });
                                                    }}
                                                />
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {(errors.time_slot_from || errors.time_slot_to) && (
                                    <p className="text-red-500 text-xs mt-2 px-1">
                                        {t((errors.time_slot_from || errors.time_slot_to)?.message.split('#')[0], {
                                            role: JSON.parse((errors.time_slot_from || errors.time_slot_to)?.message.split('#')[1] || '{}')?.role,
                                            direction: JSON.parse((errors.time_slot_from || errors.time_slot_to)?.message.split('#')[1] || '{}')?.direction
                                        })}
                                    </p>
                                )}
                            </div>
                        </div>
                    </DialogBody>
                </div>

                {leaveConfirmation?.length > 0 && (
                    <div className="mx-6 mb-4 rounded-lg border border-amber-200 bg-amber-50 p-4 text-amber-900 shadow-sm mt-4">
                        <div className="flex items-center gap-2 mb-3">
                            <svg className="h-5 w-5 text-amber-600 flex-shrink-0" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z" />
                            </svg>
                            <h3 className="font-semibold text-amber-800">{t("schedule_conflict_title")}</h3>
                        </div>
                        <ul className="divide-y divide-amber-200/60 rounded-md bg-amber-100/50 px-3 py-1 mb-3">
                            {leaveConfirmation.map((userObj) => (
                                <li key={userObj.user_id} className="flex flex-col sm:flex-row sm:items-center justify-between py-2 text-sm gap-1 sm:gap-4">
                                    <div className="flex items-center gap-1.5 flex-wrap">
                                        <span className="font-medium text-gray-900">{userObj.name}</span>
                                        {(userObj.user_type || userObj.role) && (
                                            <span className="inline-flex items-center rounded-md bg-amber-200/70 px-1.5 py-0.5 text-xs font-medium text-amber-800">
                                                {[userObj.user_type, userObj.role].filter(Boolean).join(" • ")}
                                            </span>
                                        )}
                                    </div>
                                    <div className="text-xs sm:text-sm font-medium text-amber-700 whitespace-nowrap">
                                        {moment(userObj.leave_from).format("DD MMM YYYY")} – {moment(userObj.leave_to).format("DD MMM YYYY")}
                                    </div>
                                </li>
                            ))}
                        </ul>
                        <p className="text-sm text-amber-800">
                            <Trans t={t} i18nKey="force_assign_notice">
                                To proceed, select available users or enable <strong className="font-semibold underline decoration-amber-500 underline-offset-2">Force Assign</strong> to override their leave status.
                            </Trans>
                        </p>
                    </div>
                )}

                <DialogFooter className="flex justify-end gap-4 border-t pt-4">
                    <button type="button" onClick={onClose} className="px-5 py-2 rounded-lg text-gray-700 hover:bg-gray-100">
                        {ct("notNowButton")}
                    </button>
                    <button type="submit" className="px-5 py-2 rounded-lg bg-yellow-400 hover:bg-yellow-500 text-gray-900 font-medium shadow-sm">
                        {t("assign_now_button")}
                    </button>
                    {leaveConfirmation?.length > 0 && (
                        <button type="button" className="px-5 py-2 rounded-lg bg-red-400 hover:bg-red-500 text-white font-medium shadow-sm" onClick={handleForceAssign}>
                            {t("force_assign_button")}
                        </button>
                    )}
                </DialogFooter>
            </form>
        </Dialog>
    );
}

export default AssignFinanceTeamMemberModal;